/**
 * Calcula idade baseada na data de nascimento
 * @param {string} dataNascimento
 * @returns {number|null}
 */
const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;

  const hoje = new Date();
  const nascimento = new Date(dataNascimento);

  if (isNaN(nascimento.getTime())) {
    return null;
  }

  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const mes = hoje.getMonth() - nascimento.getMonth();

  if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
    idade--;
  }

  return idade;
};

/**
 * Verifica se possui 45 anos ou mais
 */
export const tem45AnosOuMais = (dataNascimento) => {
  const idade = calcularIdade(dataNascimento);
  return idade !== null && idade >= 45;
};

/**
 * Verifica se deve exibir documento reservista
 * Regra: Homens com menos de 45 anos
 */
/**
 * O form usa radio Masculino/Feminino; o cadastro (banco) guarda M/F — aceita os dois
 */
export const isMasculino = (sexo) => {
  const valor = String(sexo || '').trim().toLowerCase();
  return valor === 'masculino' || valor === 'm';
};

const shouldShowReservista = (formData) => {
  if (!isMasculino(formData?.sexo)) return false;

  return !tem45AnosOuMais(formData?.dataNascimento);
};

/**
 * Verifica se reservista é obrigatório
 */
const isReservistaRequired = (formData) => {
  return shouldShowReservista(formData);
};

/**
 * Verifica se documento condicional deve aparecer
 */
export const shouldShowConditionalDocument = (doc, formData) => {
  // Regra especial para reservista
  if (doc.id === 'reservista' || doc.name?.toLowerCase().includes('reservista')) {
    return shouldShowReservista(formData);
  }

  // Documento que fica sempre visível, mas cuja obrigatoriedade depende de vários
  // campos (ver isConditionalDocumentRequired). Ex: DBE obrigatório se qualquer um
  // de vários tipos de alteração for marcado.
  if (doc.dependsOnAny) {
    return true;
  }

  // Sem dependência → sempre mostra
  if (!doc.dependsOn) {
    return true;
  }

  return atendeDependsOn(doc.dependsOn, formData);
};

/**
 * Avalia uma condição de dependsOn ({ field, value | values | notValue })
 */
const atendeCondicao = (condicao, formData) => {
  const dependValue = formData[condicao.field];

  // Múltiplos valores possíveis
  if (condicao.values) {
    return condicao.values.includes(dependValue);
  }

  // Qualquer valor diferente de notValue (ex: catálogo de Deficiência, onde
  // "possui" é "qualquer opção exceto Não Possui") — exige resposta explícita,
  // não dispara enquanto o campo ainda não foi respondido (undefined).
  if (condicao.notValue !== undefined) {
    return dependValue !== undefined && dependValue !== condicao.notValue;
  }

  // Valor único
  return dependValue === condicao.value;
};

/**
 * dependsOn pode ser uma condição ou uma lista delas (todas precisam valer). A lista
 * evita que o valor de um campo escondido por showWhen (o form não limpa campos que
 * somem) continue exigindo documento: ex. faculdadeForaMS só vale se tipoCandidato
 * for BACHAREL.
 */
const atendeDependsOn = (dependsOn, formData) => {
  const condicoes = Array.isArray(dependsOn) ? dependsOn : [dependsOn];
  return condicoes.every(condicao => atendeCondicao(condicao, formData));
};

/**
 * Verifica se documento condicional é obrigatório
 */
export const isConditionalDocumentRequired = (doc, formData) => {
  // Regra especial para reservista
  if (doc.id === 'reservista' || doc.name?.toLowerCase().includes('reservista')) {
    return isReservistaRequired(formData);
  }

  // Obrigatório (quando required=true) se QUALQUER UM dos campos listados estiver marcado (true ou 'SIM')
  if (doc.dependsOnAny) {
    if (!doc.required) return false;
    return doc.dependsOnAny.some(field => {
      const value = formData[field];
      return value === true || value === 'SIM';
    });
  }

  // Documento com dependência
  if (doc.required && doc.dependsOn) {
    return atendeDependsOn(doc.dependsOn, formData);
  }

  // Documento sem dependência
  return doc.required === true;
};